import { useTranslation } from 'react-i18next';

export type GroupingType = 'none' | 'status' | 'date' | 'category';

export interface DateGroup {
  key: string;
  label: string;
  startDate: Date;
  endDate: Date | null; // null means "and later"
}

/**
 * Groups items by status
 */
export function groupByStatus<T>(
  items: T[],
  getStatus: (item: T) => string
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  
  items.forEach(item => {
    const status = getStatus(item);
    if (!groups.has(status)) {
      groups.set(status, []);
    }
    groups.get(status)!.push(item);
  });
  
  return groups;
}

/**
 * Groups items by date into categories: Today, This Week, This Month, Older
 */
export function groupByDate<T>(
  items: T[],
  getDate: (item: T) => Date | string | null | undefined
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(today);
  thisWeek.setDate(today.getDate() - 7);
  const thisMonth = new Date(today);
  thisMonth.setMonth(today.getMonth() - 1);

  items.forEach(item => {
    const dateValue = getDate(item);
    if (!dateValue) {
      // Items without dates go to "Older"
      if (!groups.has('older')) {
        groups.set('older', []);
      }
      groups.get('older')!.push(item);
      return;
    }

    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (isNaN(date.getTime())) {
      // Invalid dates go to "Older"
      if (!groups.has('older')) {
        groups.set('older', []);
      }
      groups.get('older')!.push(item);
      return;
    }

    let groupKey: string;
    if (date >= today) {
      groupKey = 'today';
    } else if (date >= thisWeek) {
      groupKey = 'thisWeek';
    } else if (date >= thisMonth) {
      groupKey = 'thisMonth';
    } else {
      groupKey = 'older';
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(item);
  });

  return groups;
}

/**
 * Groups items by category
 */
export function groupByCategory<T>(
  items: T[],
  getCategory: (item: T) => string | null | undefined
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  
  items.forEach(item => {
    const category = getCategory(item) || 'uncategorized';
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category)!.push(item);
  });
  
  return groups;
}

/**
 * Gets i18n-aware label for a group key
 */
export function getGroupLabel(
  key: string,
  type: 'status' | 'date' | 'category',
  t?: (key: string) => string
): string {
  if (t) {
    const translationKey = `grouping.${type}.${key}`;
    const translated = t(translationKey);
    if (translated !== translationKey) {
      return translated;
    }
  }

  // Fallback labels
  switch (type) {
    case 'date':
      switch (key) {
        case 'today':
          return 'Today';
        case 'thisWeek':
          return 'This Week';
        case 'thisMonth':
          return 'This Month';
        case 'older':
          return 'Older';
        default:
          return key;
      }
    case 'status':
      // Capitalize first letter
      return key.charAt(0).toUpperCase() + key.slice(1);
    case 'category':
      return key;
    default:
      return key;
  }
}

/**
 * Hook to get grouping labels with i18n
 */
export function useGroupLabels() {
  const { t } = useTranslation();
  
  return {
    getStatusLabel: (key: string) => getGroupLabel(key, 'status', t),
    getDateLabel: (key: string) => getGroupLabel(key, 'date', t),
    getCategoryLabel: (key: string) => getGroupLabel(key, 'category', t),
  };
}
